import Phaser from 'phaser';
import { POPUP_OVERLAY_DEPTH } from '@constants/common.const';
import { BlurBackground } from '../BlurBackground';
import type { ModalSheetConfig } from './modal-sheet.types';

/**
 * Manages the scrim overlay and optional blurred background capture.
 */
export class ModalSheetScrimManager<TemplateData> {
  public readonly scrim: Phaser.GameObjects.Rectangle;
  private blurBg: BlurBackground;

  constructor(
    private scene: Phaser.Scene,
    private cfg: Required<ModalSheetConfig<TemplateData>>,
    private onScrimTap?: () => void,
  ) {
    const { width, height } = this.scene.scale;
    this.scrim = this.scene.add
      .rectangle(0, 0, width, height, this.cfg.scrimColor, this.cfg.scrimAlpha)
      .setOrigin(0, 0)
      .setDepth(POPUP_OVERLAY_DEPTH)
      .setAlpha(0)
      .setInteractive({ useHandCursor: true });

    if (this.onScrimTap) {
      this.scrim.on('pointerdown', this.onScrimTap);
    }

    this.blurBg = new BlurBackground(this.scene, '__modal_blur_rt__');
  }

  public updateSize(width: number, height: number): void {
    this.scrim.setSize(width, height).setPosition(0, 0);
    this.blurBg.updateSize(width, height);
  }

  public show(): void {
    this.scrim.setVisible(true);
    this.blurBg.image?.setVisible(true);
  }

  public hideBlur(): void {
    this.blurBg.destroy();
  }

  public hide(): void {
    this.scrim.setAlpha(0).setVisible(false);
    this.blurBg.destroy();
  }

  /**
   * Capture the current scene into a render texture and show it with a blur under the scrim.
   */
  public captureAndBlurScene(gameScene: Phaser.Scene): void {
    this.blurBg.capture(gameScene, {
      uiScene: this.scene,
      exclude: [this.scrim],
    });
  }

  public destroy(): void {
    if (this.onScrimTap) {
      this.scrim.off('pointerdown', this.onScrimTap);
    }
    this.blurBg.destroy();
    if (this.scrim) {
      this.scrim.destroy();
    }
  }
}
